import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import walletProductDetailDialogMessages from '../../../messages/wallet/wallet-product-detail-dialog.messages';
import {
  GlobalActions,
  Product,
  ProductCategoryType,
  ProductDetails,
  ProductDetailsSections,
  ProductTransactionsList,
  ProductUnderlyingGroupsList,
  TabItem,
} from '../../../model';
import {
  getCategoryForSelectedProduct,
  getCurrentState,
  getSelectedWalletSection,
  getTransactions,
  getUnderlyingGroups,
  messagesForWalletError,
  myWalletSliceActions,
  retrieveProductTransactions,
  retrieveProductUnderlying,
  sharedAccountsSliceActions,
} from '../../../store/features/wallet';
import { isEmptyObject } from '../../../utils/utils';
import { useAppDispatch, useAppSelector } from '../../redux';

const pdfViewCategoriesVisible: ProductCategoryType[] = [
  "LiquidAlternative",
  "MutualFunds"
];

const underlyingCategoriesVisible: ProductCategoryType[] = [
  'AssetManagement',
  'FundsManagement',
  'UnitLinked',
];

export interface ProductDetailsLogic {
  tabs: TabItem<ProductDetailsSections>[];
  selectedSection: ProductDetailsSections;

  flagOpenProductDetails: boolean;
  productDetails: ProductDetails;
  isLoadingTransactions: boolean;
  isLoadingUnderlying: boolean;
  transactionList: ProductTransactionsList;
  underlyingGroupsList: ProductUnderlyingGroupsList;
  selectedProduct: Product;

  onSelectedSection: (selectedSection: ProductDetailsSections) => void;
  onTransactionsPaginationChange: (page: number) => void;
  onTransactionsPaginationNext: () => void;
  onTransactionsPaginationPrev: () => void;
}

export const useProductDetailsLogic = (
  globalActions: GlobalActions
): ProductDetailsLogic => {
  const dispatch = useAppDispatch();
  const { showToast, closeToast } = globalActions;
  const { formatMessage } = useIntl();

  const {
    flagOpenProductDetails,
    selectedProduct,
    productDetails,
    productTransactionsError,
    productTransactionsRequestStatus,
    productUnderlyingRequestStatus,
    productUnderlyingError,
    productDetailsSelectedSection,
  }:any = useAppSelector(getCurrentState);

  const transactionList: ProductTransactionsList =
    useAppSelector(getTransactions);

  const mappedTransactionList = useMemo(() => {
    if (!transactionList?.transactions) return transactionList;

    const getCategoryLabel = (category: string): string => {
      /*if (category === 'VERS') {
        return 'Versamento';
      }
      // Aggiungi altre categorie qui in futuro*/
      return category;
    };

    return {
      ...transactionList,
      transactions: transactionList.transactions.map(transaction => ({
        ...transaction,
        category: getCategoryLabel(transaction.category)
      }))
    };
  }, [transactionList]);

  const underlyingGroupsList: ProductUnderlyingGroupsList =
    useAppSelector(getUnderlyingGroups);

  const productCategory:any = useAppSelector(getCategoryForSelectedProduct);
  const selectedWalletSection:any = useAppSelector(getSelectedWalletSection);

  const actions = useMemo(
    () =>
      selectedWalletSection === 'MY_WALLET'
        ? myWalletSliceActions
        : sharedAccountsSliceActions,
    [selectedWalletSection]
  );

  const tabs = useMemo<TabItem<ProductDetailsSections>[]>(() => {
    const transactionsTab: TabItem<ProductDetailsSections> = {
      type: 'TRANSACTIONS',
      label: formatMessage(walletProductDetailDialogMessages.transactionsTab),
    };

    const pdfViewTab: TabItem<ProductDetailsSections> = {
      type: 'PDFVIEW',
      label: formatMessage(walletProductDetailDialogMessages.pdfViewTab),
    };

    const underlyingTab: TabItem<ProductDetailsSections> = {
      type: 'UNDERLYING',
      label: formatMessage(walletProductDetailDialogMessages.underlyingTab),
    };

    const kidTab: TabItem<ProductDetailsSections> = {
      type: 'KID',
      label: formatMessage(walletProductDetailDialogMessages.kidTab),
    };

    const tabList = [transactionsTab];
    if (pdfViewCategoriesVisible.includes((productCategory?.type)?.replaceAll(" ",""))) {
      tabList.push(pdfViewTab);
    }

    if (underlyingCategoriesVisible.includes(productCategory?.type)) {
      tabList.push(underlyingTab);
    }
    if (selectedProduct?.isKidExisting && selectedProduct?.isPriips) {
      tabList.push(kidTab);
    }

    return tabList;
  }, [formatMessage, productCategory, selectedProduct]);

  useEffect(() => {
    if (flagOpenProductDetails) {
      dispatch(retrieveProductTransactions());
    }
  }, [dispatch, flagOpenProductDetails]);

  const onTransactionsPaginationChange = useCallback(
    (page: number) => {
      dispatch(actions.setPageTransactions({ page }));
    },
    [dispatch, actions]
  );

  const onTransactionsPaginationNext = useCallback(() => {
    dispatch(
      actions.setPageTransactions({
        page: transactionList.pageInfo.page + 1,
      })
    );
  }, [dispatch, transactionList, actions]);

  const onTransactionsPaginationPrev = useCallback(() => {
    dispatch(
      actions.setPageTransactions({
        page: transactionList.pageInfo.page - 1,
      })
    );
  }, [dispatch, transactionList, actions]);



  const onSelectedSection = useCallback(
    (sectionType: ProductDetailsSections) => {
      dispatch(actions.setProductDetailsSelectedSection(sectionType));
      if (
        sectionType === 'UNDERLYING' &&
        isEmptyObject(
          productDetails.underlyingGroups?.underlyingGroupsPaginated.items
        )
      ) {
        dispatch(retrieveProductUnderlying());
      }
    },
    [actions, dispatch, productDetails]
  );

  useEffect(() => {
    if (productTransactionsError) {
      const errorMessages = messagesForWalletError(productTransactionsError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(actions.clearProductTransactionsError());
    }
  }, [
    closeToast,
    dispatch,
    formatMessage,
    productTransactionsError,
    showToast,
    actions,
  ]);

  useEffect(() => {
    if (productUnderlyingError) {
      const errorMessages = messagesForWalletError(productUnderlyingError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(actions.clearProductUnderlyingError());
    }
  }, [
    closeToast,
    dispatch,
    formatMessage,
    productUnderlyingError,
    showToast,
    actions,
  ]);

  return {
    tabs,
    flagOpenProductDetails,
    selectedProduct,
    productDetails,
    selectedSection: productDetailsSelectedSection,
    underlyingGroupsList,
    transactionList : mappedTransactionList,
    onSelectedSection,
    onTransactionsPaginationChange,
    isLoadingTransactions: productTransactionsRequestStatus === 'loading',
    isLoadingUnderlying: productUnderlyingRequestStatus === 'loading',
    onTransactionsPaginationNext,
    onTransactionsPaginationPrev,
  };
};
